import React from 'react';
import { store } from '@/store/store';
import workApi, { useListBoardsQuery, useListProjectsQuery, useListTagsQuery, useListTasksQuery } from '@/store/api/work/work.api';
import type { TypeBoardInput, TypeProjectInput, TypeTaskPatch } from '@/types/work/types.work.input';
import notify from '@/lib/notify/notify';
import { canClose, canDependOn, closeReason } from '@/function/work/task.deps';
import { canReparent } from '@/function/work/task.tree';
import { cleanTagName, findTagByName } from '@/function/work/task.tags';
import type { TypeBoard, TypeBoardColumn } from '@/types/work/types.work.board';
import { EMPTY_MAP } from '@/function/work/task.map.constants';
import { EMPTY_CHECKLIST, EMPTY_MEETING, EMPTY_TABLE } from '@/function/work/task.note.constants';
import type { TypeNote, TypeNoteKind } from '@/types/work/types.work.note';
import type { TypeProject, TypeProjectStatus } from '@/types/work/types.work.project';
import type { TypeTag, TypeTagColor } from '@/types/work/types.work.tag';
import type { TypeTask, TypeTaskEdit, TypeTaskPriority, TypeTaskState } from '@/types/work/types.work.task';
import { sendLocal, settleLocal, touchLocal } from '@/store/api/work/work.local';

export { setColumnOrder, useColumnOrder } from './work.views';

const EMPTY_TASKS: TypeTask[] = [];
const EMPTY_PROJECTS: TypeProject[] = [];
const EMPTY_BOARDS: TypeBoard[] = [];
const EMPTY_TAGS: TypeTag[] = [];

const now = () => new Date().toISOString();
export const fresh = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4)}`;

const read = () => {
    const state = store.getState();
    return {
        tasks: workApi.endpoints.listTasks.select()(state).data ?? EMPTY_TASKS,
        projects: workApi.endpoints.listProjects.select()(state).data ?? EMPTY_PROJECTS,
        boards: workApi.endpoints.listBoards.select()(state).data ?? EMPTY_BOARDS,
        tags: workApi.endpoints.listTags.select()(state).data ?? EMPTY_TAGS,
    };
};

export const taskById = (taskId: string): TypeTask | undefined => read().tasks.find((task) => task.id === taskId);

const send = async <T>(action: any, loadingId: string | null = null): Promise<T | null> => {
    try {
        return await (store.dispatch(action) as any).unwrap();
    } catch (error) {
        notify.fromRejection(loadingId, error);
        return null;
    }
};

const patchTask = (taskId: string, task: TypeTaskPatch, seq?: number): Promise<TypeTask | null> =>
    send<TypeTask>(workApi.endpoints.updateTask.initiate({ id: taskId, task, seq }));

const pending = new Map<string, TypeTaskPatch>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

export const TYPING_MS = 600;

const writeNow = (taskId: string, task: Partial<TypeTask>): void => {
    store.dispatch(workApi.util.updateQueryData('listTasks', undefined, (draft) => {
        const entry = draft.find((item) => item.id === taskId);
        if (entry) Object.assign(entry, task);
    }));
};

const flush = async (taskId: string): Promise<void> => {
    const timer = timers.get(taskId);
    if (timer) clearTimeout(timer);
    timers.delete(taskId);

    const task = pending.get(taskId);
    pending.delete(taskId);
    if (!task) return;

    const seq = sendLocal(taskId);
    const saved = await patchTask(taskId, task, seq);
    settleLocal(taskId);

    if (!saved) store.dispatch(workApi.util.invalidateTags(['Task']));
};

const queue = (taskId: string, task: TypeTaskPatch, delay = TYPING_MS): void => {
    touchLocal(taskId, Object.keys(task));
    writeNow(taskId, task);
    pending.set(taskId, { ...(pending.get(taskId) ?? {}), ...task });

    const timer = timers.get(taskId);
    if (timer) clearTimeout(timer);

    if (delay <= 0) { void flush(taskId); return; }
    timers.set(taskId, setTimeout(() => { void flush(taskId); }, delay));
};

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => { pending.forEach((_task, taskId) => { void flush(taskId); }); });
}

export async function moveTask(taskId: string, to: TypeTaskState, beforeId?: string | null): Promise<boolean> {
    const moving = taskById(taskId);
    if (!moving) return false;
    if (to === 'done' && !canClose(read().tasks, moving)) return false;

    const moved = await send<TypeTask[]>(workApi.endpoints.moveTask.initiate({ id: taskId, state: to, beforeId: beforeId ?? null }));
    return Boolean(moved);
}

export async function placeTask(taskId: string, parentId: string | null, beforeId?: string | null): Promise<void> {
    if (!canReparent(read().tasks, taskId, parentId)) return;
    await send(workApi.endpoints.placeTask.initiate({ id: taskId, parentId, beforeId: beforeId ?? null }));
}

export function setAssignee(taskId: string, assigneeId: string | null): void {
    void patchTask(taskId, { assigneeId });
}

export function setPriority(taskId: string, priority: TypeTaskPriority): void {
    void patchTask(taskId, { priority });
}

export async function setDue(taskId: string, dueAt: string | null): Promise<boolean> {
    const before = taskById(taskId)?.dueAt ?? null;
    writeNow(taskId, { dueAt });
    const saved = await patchTask(taskId, { dueAt });
    if (!saved) writeNow(taskId, { dueAt: before });
    return Boolean(saved);
}

export function setNotes(taskId: string, notes: string): void {
    queue(taskId, { notes });
}

export function setDescription(taskId: string, description: string): void {
    queue(taskId, { description });
}

export function setHours(taskId: string, estimateHours: number | null): void {
    void patchTask(taskId, { estimateHours });
}

export function setTitle(taskId: string, title: string): void {
    queue(taskId, { title });
}

export async function toggleDone(taskId: string): Promise<boolean> {
    const task = taskById(taskId);
    if (!task) return false;
    const to = task.state === 'done' ? 'doing' : 'done';
    if (to === 'done' && !canClose(read().tasks, task)) return false;
    const before = { state: task.state, doneAt: task.doneAt };
    writeNow(taskId, { state: to, doneAt: to === 'done' ? new Date().toISOString() : null });
    const moved = await moveTask(taskId, to, null);
    if (!moved) writeNow(taskId, before);
    return moved;
}

export const whyBlocked = (taskId: string): string | null => {
    const task = taskById(taskId);
    return task ? closeReason(read().tasks, task) : null;
};

export const patchNotebook = (taskId: string, change: (notebook: TypeNote[]) => TypeNote[], delay = 0): void => {
    const task = taskById(taskId);
    if (!task) return;
    queue(taskId, { notebook: change(task.notebook) }, delay);
};

export const addEntry = async (
    taskId: string,
    change: (notebook: TypeNote[]) => TypeNote[],
    pick: (notebook: TypeNote[]) => string,
): Promise<string> => {
    const task = taskById(taskId);
    if (!task) return '';

    await flush(taskId);

    const current = taskById(taskId);
    if (!current) return '';

    const wanted = change(current.notebook);
    touchLocal(taskId, ['notebook']);
    writeNow(taskId, { notebook: wanted });

    const seq = sendLocal(taskId);
    const saved = await patchTask(taskId, { notebook: wanted }, seq);
    settleLocal(taskId);
    return saved ? pick(saved.notebook) : '';
};

export const noteOf = (notebook: TypeNote[], noteId: string): TypeNote | undefined =>
    notebook.find((note) => note.id === noteId);

export const entryKey = (): string => `${Date.now()}${String(Math.floor(Math.random() * 1e6)).padStart(6, '0')}`;

export function addNote(taskId: string, kind: TypeNoteKind, title: string, language = '', body = '', seed: Partial<Omit<TypeNote, 'id' | 'kind'>> = {}): Promise<string> {
    return addEntry(
        taskId,
        (notebook) => [...notebook, {
            id: fresh('nt'), kind, title, language, body, map: EMPTY_MAP, checklist: EMPTY_CHECKLIST, table: EMPTY_TABLE, meeting: EMPTY_MEETING, createdAt: now(), ...seed,
        }],
        (notebook) => notebook[notebook.length - 1]?.id ?? '',
    );
}

export function updateNote(taskId: string, noteId: string, change: Partial<Omit<TypeNote, 'id'>>): void {
    patchNotebook(taskId, (notebook) => notebook.map((note) => (note.id === noteId ? { ...note, ...change } : note)), TYPING_MS);
}

export function reviseNote(taskId: string, noteId: string, change: (note: TypeNote) => TypeNote, delay = TYPING_MS): void {
    patchNotebook(taskId, (notebook) => notebook.map((note) => (note.id === noteId ? change(note) : note)), delay);
}

export function removeNote(taskId: string, noteId: string): void {
    patchNotebook(taskId, (notebook) => notebook.filter((note) => note.id !== noteId));
}

export async function createTag(name: string, color?: TypeTagColor, projectId?: string | null): Promise<string> {
    const clean = cleanTagName(name);
    if (!clean) return '';

    const already = findTagByName(read().tags.filter((tag) => (tag.projectId ?? null) === (projectId ?? null)), clean);
    if (already) return already.id;

    const tag = await send<TypeTag>(workApi.endpoints.createTag.initiate({ name: clean, color, projectId }));
    return tag?.id ?? '';
}

export async function renameTag(tagId: string, name: string): Promise<void> {
    const clean = cleanTagName(name);
    if (!clean) return;
    await send(workApi.endpoints.updateTag.initiate({ id: tagId, name: clean }));
}

export async function recolorTag(tagId: string, color: TypeTagColor): Promise<void> {
    await send(workApi.endpoints.updateTag.initiate({ id: tagId, color }));
}

export async function removeTag(tagId: string): Promise<void> {
    await send(workApi.endpoints.removeTag.initiate(tagId));
}

export function toggleTaskTag(taskId: string, tagId: string): void {
    const task = taskById(taskId);
    if (!task) return;

    const tags = task.tags.includes(tagId) ? task.tags.filter((id) => id !== tagId) : [...task.tags, tagId];
    queue(taskId, { tags }, 0);
}

export function detachTaskTag(taskId: string, tagId: string): void {
    const task = taskById(taskId);
    if (!task) return;
    queue(taskId, { tags: task.tags.filter((id) => id !== tagId) }, 0);
}

export async function tagTaskByName(taskId: string, name: string, color?: TypeTagColor, projectId?: string | null): Promise<string> {
    const tagId = await createTag(name, color, projectId);
    if (!tagId) return '';

    const task = taskById(taskId);
    if (task && !task.tags.includes(tagId)) await patchTask(taskId, { tags: [...task.tags, tagId] });

    return tagId;
}

const setProjectTags = async (projectId: string, tags: string[]): Promise<void> => {
    await send(workApi.endpoints.setProjectTags.initiate({ id: projectId, tags }));
};

export async function toggleProjectTag(projectId: string, tagId: string): Promise<void> {
    const project = read().projects.find((entry) => entry.id === projectId);
    if (!project) return;

    await setProjectTags(projectId, project.tags.includes(tagId)
        ? project.tags.filter((id) => id !== tagId)
        : [...project.tags, tagId]);
}

export async function detachProjectTag(projectId: string, tagId: string): Promise<void> {
    const project = read().projects.find((entry) => entry.id === projectId);
    if (!project || !project.tags.includes(tagId)) return;

    await setProjectTags(projectId, project.tags.filter((id) => id !== tagId));
}

export async function tagProjectByName(
    projectId: string,
    name: string,
    color?: TypeTagColor,
    scope: string | null = projectId,
): Promise<string> {
    const tagId = await createTag(name, color, scope);
    if (!tagId) return '';

    const project = read().projects.find((entry) => entry.id === projectId);
    if (project && !project.tags.includes(tagId)) await setProjectTags(projectId, [...project.tags, tagId]);

    return tagId;
}

const asInput = (board: TypeBoard, columns?: TypeBoardColumn[]): TypeBoardInput => ({
    name: board.name,
    hint: board.hint,
    columns: (columns ?? board.columns).map((column) => ({ id: column.id, label: column.label, hint: column.hint, color: column.color, status: column.status })),
});

const boardById = (boardId: string): TypeBoard | undefined => read().boards.find((board) => board.id === boardId);

export async function addBoard(name: string, hint: string, columns: { label: string; hint: string; status: TypeProjectStatus }[]): Promise<string> {
    const created = await send<TypeBoard>(workApi.endpoints.createBoard.initiate({
        name,
        hint,
        columns: columns.map((column) => ({ label: column.label, hint: column.hint, status: column.status })),
    }));
    return created?.id ?? '';
}

export async function setBoardColumns(
    boardId: string,
    columns: TypeBoardColumn[],
    registry?: { name: string; hint: string },
): Promise<void> {
    const board = boardById(boardId);
    if (!board) return;

    await send(workApi.endpoints.updateBoard.initiate({
        id: boardId,
        board: { ...asInput(board, columns), ...(registry ?? {}) },
    }));
}

export async function addBoardColumn(boardId: string, label: string): Promise<string | null> {
    const board = boardById(boardId);
    if (!board) return null;

    const updated = await send<{ board: TypeBoard }>(workApi.endpoints.updateBoard.initiate({
        id: boardId,
        board: asInput(board, [...board.columns, { id: '', label, hint: '', color: null, status: 'active' }]),
    }));

    const columns = updated?.board.columns ?? [];
    return columns.length > board.columns.length ? columns[columns.length - 1].id : null;
}

export async function moveBoardColumn(boardId: string, columnId: string, beforeId: string | null): Promise<void> {
    const board = boardById(boardId);
    if (!board) return;

    const moving = board.columns.find((column) => column.id === columnId);
    if (!moving) return;

    const rest = board.columns.filter((column) => column.id !== columnId);
    const at = beforeId ? rest.findIndex((column) => column.id === beforeId) : -1;
    rest.splice(at < 0 ? rest.length : at, 0, moving);

    await setBoardColumns(boardId, rest);
}

export async function renameBoardColumn(boardId: string, columnId: string, label: string): Promise<void> {
    const board = boardById(boardId);
    if (!board) return;
    await setBoardColumns(boardId, board.columns.map((column) => (column.id === columnId ? { ...column, label } : column)));
}

export async function addBoardLike(
    name: string,
    hint: string,
    columns: { label: string; hint: string; color: string | null; status?: TypeProjectStatus }[],
): Promise<string> {
    const created = await send<TypeBoard>(workApi.endpoints.createBoard.initiate({ name, hint, columns }));
    return created?.id ?? '';
}

export async function removeBoard(boardId: string): Promise<void> {
    await send(workApi.endpoints.removeBoard.initiate(boardId));
}

export async function placeOnBoard(projectId: string, boardId: string, columnId: string, beforeId: string | null): Promise<void> {
    await send(workApi.endpoints.placeProject.initiate({ id: projectId, boardId, columnId, beforeId }));
}

export async function createProject(project: TypeProjectInput, column: string): Promise<string> {
    const created = await send<TypeProject>(workApi.endpoints.createProject.initiate({ project, column }));
    return created?.id ?? '';
}

export async function updateProject(id: string, project: TypeProjectInput): Promise<boolean> {
    return Boolean(await send(workApi.endpoints.updateProject.initiate({ id, project })));
}

export async function patchProject(id: string, change: Partial<TypeProjectInput>): Promise<boolean> {
    const before = read().projects.find((project) => project.id === id);
    if (!before) return false;
    const { column: _column, order: _order, archivedAt: _archivedAt, attachments: _attachments, id: _id, ...input } = before;
    const write = (next: Partial<TypeProject>) => store.dispatch(workApi.util.updateQueryData('listProjects', undefined, (draft) => {
        const entry = draft.find((project) => project.id === id);
        if (entry) Object.assign(entry, next);
    }));
    write(change);
    const saved = await updateProject(id, { ...input, ...change });
    if (!saved) write(Object.fromEntries(Object.keys(change).map((key) => [key, before[key as keyof TypeProject]])));
    return saved;
}

export async function archiveProject(id: string, archived = true, loadingId: string | null = null): Promise<boolean> {
    return Boolean(await send(workApi.endpoints.archiveProject.initiate({ id, archived }), loadingId));
}

export async function removeProject(id: string, loadingId: string | null = null): Promise<boolean> {
    return Boolean(await send(workApi.endpoints.removeProject.initiate(id), loadingId));
}

export const useProject = (projectId: string | undefined): TypeProject | null => {
    const projects = useProjects();
    return React.useMemo(() => projects.find((project) => project.id === projectId) ?? null, [projects, projectId]);
};

export async function createTask(input: {
    projectId: string;
    state: TypeTaskState;
    title: string;
    parentId?: string | null;
    description?: string;
    notes?: string;
    assigneeId?: string | null;
    priority?: TypeTaskPriority;
    dueAt?: string | null;
    estimateHours?: number | null;
    tags?: string[];
}): Promise<string> {
    const created = await send<TypeTask>(workApi.endpoints.createTask.initiate(input));
    return created?.id ?? '';
}

export function addTask(
    projectId: string,
    taskState: TypeTaskState,
    title: string,
    parentId: string | null = null,
): Promise<string> {
    return createTask({ projectId, state: taskState, title, parentId, assigneeId: store.getState().auth.user?.authUserId ?? null });
}

export async function removeTask(taskId: string, loadingId: string | null = null): Promise<number> {
    const removed = await send<{ ids: string[] }>(workApi.endpoints.removeTask.initiate(taskId), loadingId);
    return removed ? removed.ids.length || 1 : 0;
}

export async function addDependency(taskId: string, blockerId: string): Promise<boolean> {
    const tasks = read().tasks;
    if (!canDependOn(tasks, taskId, blockerId)) return false;

    const task = tasks.find((entry) => entry.id === taskId);
    if (!task) return false;

    const updated = await patchTask(taskId, { blockedBy: [...task.blockedBy, blockerId] });
    return Boolean(updated);
}

export async function removeDependency(taskId: string, blockerId: string): Promise<void> {
    const task = taskById(taskId);
    if (!task) return;
    await patchTask(taskId, { blockedBy: task.blockedBy.filter((id) => id !== blockerId) });
}

export async function archiveTask(taskId: string, loadingId: string | null = null): Promise<boolean> {
    return Boolean(await send(workApi.endpoints.archiveTask.initiate({ id: taskId, archived: true }), loadingId));
}

export async function restoreTask(taskId: string, loadingId: string | null = null): Promise<boolean> {
    return Boolean(await send(workApi.endpoints.archiveTask.initiate({ id: taskId, archived: false }), loadingId));
}

export async function editTask(taskId: string, task: TypeTaskEdit): Promise<{ tasks: TypeTask[]; dropped: number }> {
    await flush(taskId);
    return (store.dispatch(workApi.endpoints.editTask.initiate({ id: taskId, task })) as any).unwrap();
}

export const useProjects = (): TypeProject[] => useListProjectsQuery().data ?? EMPTY_PROJECTS;
export const useBoards = (): TypeBoard[] => useListBoardsQuery().data ?? EMPTY_BOARDS;
export const useTags = (): TypeTag[] => useListTagsQuery().data ?? EMPTY_TAGS;
export const useAllTasks = (): TypeTask[] => useListTasksQuery().data ?? EMPTY_TASKS;

export const useTasks = (): TypeTask[] => {
    const all = useAllTasks();
    return React.useMemo(() => all.filter((task) => !task.archivedAt), [all]);
};

export const useTask = (taskId: string | undefined): TypeTask | null => {
    const all = useAllTasks();
    return React.useMemo(() => all.find((task) => task.id === taskId) ?? null, [all, taskId]);
};

export const useWorkState = () => {
    const tasks = useListTasksQuery();
    const projects = useListProjectsQuery();
    const boards = useListBoardsQuery();
    const tags = useListTagsQuery();

    return {
        isLoading: tasks.isLoading || projects.isLoading || boards.isLoading || tags.isLoading,
        isFetching: tasks.isFetching || projects.isFetching,
        isError: tasks.isError || projects.isError,
        refetch: () => { void tasks.refetch(); void projects.refetch(); void boards.refetch(); void tags.refetch(); },
    };
};
